"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";
import { useTranslations } from "@/hooks/useTranslations";

type QuickClientFormData = {
  name: string;
  email?: string;
  phone?: string;
  address?: string;
};

interface QuickClientModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function QuickClientModal({ isOpen, onClose }: QuickClientModalProps) {
  const router = useRouter();
  const { toast } = useToast();
  const t = useTranslations();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const quickClientSchema = z.object({
    name: z.string().min(1, t("quickActions.client.nameRequired")),
    email: z.string().email(t("quickActions.client.invalidEmail")).optional().or(z.literal("")),
    phone: z.string().optional(),
    address: z.string().optional(),
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<QuickClientFormData>({
    resolver: zodResolver(quickClientSchema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      address: "",
    },
  });

  const onSubmit = async (data: QuickClientFormData) => {
    setIsSubmitting(true);

    try {
      const response = await fetch("/api/clients", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: data.name,
          email: data.email || null,
          phone: data.phone || null,
          address: data.address || null,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to create client");
      }

      const client = await response.json();

      toast({
        title: t("quickActions.client.toast.created"),
        description: t("quickActions.client.toast.createdDescription", { name: data.name }),
      });

      reset();
      onClose();
      router.push(`/clients/${client.id}`);
      router.refresh();
    } catch (error) {
      console.error("Error creating client:", error);
      toast({
        title: t("quickActions.client.toast.error"),
        description: t("quickActions.client.toast.errorDescription"),
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      reset();
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t("quickActions.client.title")}</DialogTitle>
          <DialogDescription>
            {t("quickActions.client.description")}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">
              {t("quickActions.client.nameLabel")} <span className="text-error">*</span>
            </Label>
            <Input
              id="name"
              {...register("name")}
              placeholder={t("quickActions.client.namePlaceholder")}
              disabled={isSubmitting}
            />
            {errors.name && (
              <p className="text-sm text-error">{errors.name.message}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="email">{t("quickActions.client.emailLabel")}</Label>
              <Input
                id="email"
                type="email"
                {...register("email")}
                placeholder={t("quickActions.client.emailPlaceholder")}
                disabled={isSubmitting}
              />
              {errors.email && (
                <p className="text-sm text-error">{errors.email.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">{t("quickActions.client.phoneLabel")}</Label>
              <Input
                id="phone"
                type="tel"
                {...register("phone")}
                placeholder={t("quickActions.client.phonePlaceholder")}
                disabled={isSubmitting}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="address">{t("quickActions.client.addressLabel")}</Label>
            <Input
              id="address"
              {...register("address")}
              placeholder={t("quickActions.client.addressPlaceholder")}
              disabled={isSubmitting}
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t("quickActions.client.creating")}
                </>
              ) : (
                t("quickActions.client.createButton")
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
